import { useMemo } from 'react';
import { create } from 'zustand';

// Always default to the current calendar year — avoids hardcoded year bugs across deployments
const _year     = new Date().getFullYear();
const _DEFAULT_DESDE = `${_year}-01-01`;
const _DEFAULT_HASTA = `${_year}-12-31`;

export const useFiltersStore = create((set) => ({
  desde: _DEFAULT_DESDE,
  hasta: _DEFAULT_HASTA,
  sede: null,
  negocio: null,
  gerente: null,
  categoria: null,
  centro: null,
  persona: null,
  incumplimiento: null,
  delegacion: null,

  setFilter: (key, value) => set({ [key]: value || null }),

  clearFilters: () => set({
    desde: _DEFAULT_DESDE,
    hasta: _DEFAULT_HASTA,
    sede: null,
    negocio: null,
    gerente: null,
    categoria: null,
    centro: null,
    persona: null,
    incumplimiento: null,
    delegacion: null,
  }),
}));

// Helper selector (not a hook) - returns a primitive count
// desde/hasta only count as an "active filter" when they differ from the
// current-year default — otherwise the badge shows "2" before the user
// touches anything, which looks like a stuck filter.
export const getActiveFilterCount = (state) => {
  const { desde, hasta, sede, negocio, gerente, categoria, centro, persona, incumplimiento, delegacion } = state;
  const dateIsCustom = desde !== _DEFAULT_DESDE || hasta !== _DEFAULT_HASTA;
  return [sede, negocio, gerente, categoria, centro, persona, incumplimiento, delegacion].filter(Boolean).length
    + (dateIsCustom ? 1 : 0);
};

/**
 * Stable hook for query params.
 * Selects each filter individually (primitives → always stable references),
 * then memoizes the resulting params object so React Query sees a stable key.
 */
export const useFilterParams = () => {
  const desde       = useFiltersStore(s => s.desde);
  const hasta       = useFiltersStore(s => s.hasta);
  const sede        = useFiltersStore(s => s.sede);
  const negocio     = useFiltersStore(s => s.negocio);
  const gerente     = useFiltersStore(s => s.gerente);
  const categoria   = useFiltersStore(s => s.categoria);
  const centro      = useFiltersStore(s => s.centro);
  const persona     = useFiltersStore(s => s.persona);
  const incumplimiento = useFiltersStore(s => s.incumplimiento);
  const delegacion  = useFiltersStore(s => s.delegacion);

  return useMemo(() => {
    const params = {};
    if (desde)        params.desde        = desde;
    if (hasta)        params.hasta        = hasta;
    if (sede)         params.sede         = sede;
    if (negocio)      params.negocio      = negocio;
    if (gerente)      params.gerente      = gerente;
    if (categoria)    params.categoria    = categoria;
    if (centro)       params.centro       = centro;
    if (persona)      params.persona      = persona;
    if (incumplimiento) params.incumplimiento = incumplimiento;
    if (delegacion)   params.delegacion   = delegacion;
    return params;
  }, [desde, hasta, sede, negocio, gerente, categoria, centro, persona, incumplimiento, delegacion]);
};
